/* Browser-side Gemini Live session. */
/*  */
/* Lifecycle: */
/*   1. mintToken() — POST to the mint-gemini-token Edge Function with the */
/*      user's JWT, get back an ephemeral token bound to a model + config */
/*      (server-side liveConnectConstraints, client cannot widen). */
/*   2. open() — open the BidiGenerateContentConstrained WebSocket, set up */
/*      mic capture via getUserMedia + AudioWorklet, stream 16 kHz PCM up */
/*      and pipe the server's 24 kHz PCM back through Web Audio. */
/*   3. close() — release mic, close socket, stop playback. */
/*  */
/* On the Web Audio side we receive base64 PCM (16-bit, 24 kHz mono) and */
/* schedule chained AudioBufferSourceNodes so playback is continuous even */
/* when chunks arrive bursty. */

import { requireSession } from './supabase';

/* Voice now proxies through OUR backend (WS /voice), which connects upstream to */
/* Vertex Live with a server-side Authorization header (browsers can't set WS */
/* headers, and Vertex rejects query-param tokens). The user JWT + mode ride the */
/* query string; the backend mints the Vertex token and sends the setup. No edge */
/* function, no AI Studio. */
import { BACKEND_URL as BACKEND_BASE } from './config';
const BACKEND_VOICE_WS = `${BACKEND_BASE.replace(/^http/, 'ws')}/voice`;

export type VoiceState = 'idle' | 'connecting' | 'listening' | 'speaking' | 'error';

export interface VoiceCallbacks {
  onState?: (s: VoiceState) => void;
  onTranscript?: (text: string, role: 'user' | 'assistant', final: boolean) => void;
  onError?: (err: Error) => void;
  /** Fires when a turn completes (server-side VAD said the assistant is
   *  done). Carries the final concatenated text for both sides so the
   *  caller can persist it. */
  onTurnComplete?: (turn: { user: string; assistant: string }) => void;
  /** Fires once the backend has resolved (or created) the thread this session
   *  writes into. The page adopts it — the server, not the URL, owns the thread. */
  onReady?: (info: { threadId: string }) => void;
  /** The backend refused the session (auth, ownership, unsupported mode). */
  onRefused?: (reason: string) => void;
  /** Fires when the model invokes a tool. The handler returns a value that
   *  becomes the function's `response` field in the toolResponse sent back
   *  to the model. Returning `undefined` resolves with `{ ok: true }` so
   *  the model knows the call completed. */
  onToolCall?: (name: string, args: Record<string, unknown>) => unknown | Promise<unknown>;
  /* Trames de contrôle de NOTRE proxy — refus d'accès, budget de parole — par
     opposition aux trames Vertex relayées. Le serveur tient le budget : le
     client ne fait que rendre ce qu'il annonce. */
  onServerEvent?: (event: Record<string, unknown>) => void;
}

export class WebVoiceSession {
  private ws: WebSocket | null = null;
  private ac: AudioContext | null = null;
  private mic: MediaStream | null = null;
  private capture: AudioWorkletNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private playbackTime = 0;
  private state: VoiceState = 'idle';
  private pendingAssistantText = '';
  private pendingUserText = '';
  private micMuted = false;
  private startedAt = 0;
  /* Prior text turns to seed the Live session with on setupComplete, so voice */
  /* "remembers" the text thread (the minted token carries historyConfig). Sent */
  /* once as clientContent (turnComplete:false) before any audio. */
  private initialHistory: { role: 'user' | 'model'; text: string }[] = [];
  private seeded = false;
  /* True while we are intentionally tearing down — suppresses the "connection */
  /* closed" error banner on a clean stop() or normal end-of-session. */
  private stopping = false;
  /* Latest WS close info, for diagnostics. */
  private lastClose: { code: number; reason: string } | null = null;
  /* Session-resumption handle from Gemini (stored for future reconnection). */
  private resumeHandle: string | null = null;

  /* mode selects the voice persona + toolset minted server-side */
  /* ('general' | 'parcours' | 'teacher' | 'admin'). */
  constructor(
    private readonly cb: VoiceCallbacks = {},
    private readonly mode: string = 'general',
    private readonly threadId: string | null = null,
    /** Paramètres de session supplémentaires (chat élève : studentId, threadId). */
    private readonly extraQuery: Record<string, string> = {},
  ) {}

  /** Latest WS close info ({code, reason}) — null until the socket closes. */
  getLastClose(): { code: number; reason: string } | null {
    return this.lastClose;
  }

  /** When true, captured PCM is dropped before being sent to Gemini. The mic
   *  stream stays open so the browser AEC keeps tracking the speaker — but
   *  background noise can't trigger a turn / interrupt the assistant. */
  setMicMuted(b: boolean): void {
    this.micMuted = b;
  }
  isMicMuted(): boolean {
    return this.micMuted;
  }
  /** Milliseconds since this session started. 0 if not running. */
  elapsedMs(): number {
    return this.startedAt > 0 ? Date.now() - this.startedAt : 0;
  }

  private setState(s: VoiceState) {
    this.state = s;
    this.cb.onState?.(s);
  }

  async start(opts?: { initialHistory?: { role: 'user' | 'model'; text: string }[] }): Promise<void> {
    if (this.state !== 'idle') return;
    this.initialHistory = opts?.initialHistory ?? [];
    this.seeded = false;
    this.stopping = false;
    this.lastClose = null;
    this.setState('connecting');
    this.startedAt = Date.now();
    this.pendingAssistantText = '';
    this.pendingUserText = '';

    try {
      /* Mic FIRST — Safari (iOS especially) treats getUserMedia as needing a */
      /* fresh user-gesture context. If we await anything else before it */
      /* (token mint, WebSocket open), iOS considers the gesture "spent" and */
      /* throws NotFoundError "The object can not be found here." Calling it */
      /* first preserves the gesture chain. */
      await this.runStep('get-mic', () => this.getMic());
      await this.runStep('open-websocket', () => this.openSocket());
      await this.runStep('setup-audio', () => this.setupAudio());
      this.setState('listening');
    } catch (e) {
      console.error('[voice] start failed:', e);
      this.setState('error');
      this.cb.onError?.(e instanceof Error ? e : new Error(String(e)));
      await this.stop();
    }
  }

  /* Wraps a step with a label so failures are immediately attributable to the */
  /* right phase (token mint / WS / mic). Native DOMException messages like */
  /* "The object can not be found here" are otherwise unhelpfully terse. */
  private async runStep<T>(label: string, fn: () => Promise<T>): Promise<T> {
    try {
      return await fn();
    } catch (e) {
      const native = e instanceof Error ? e : new Error(String(e));
      const wrapped = new Error(`[${label}] ${native.name}: ${native.message}`);
      (wrapped as Error & { cause?: unknown }).cause = native;
      throw wrapped;
    }
  }

  async stop(): Promise<void> {
    this.stopping = true;
    try { this.capture?.disconnect(); } catch { /* ignore */ }
    try { this.source?.disconnect(); } catch { /* ignore */ }
    if (this.mic) {
      for (const t of this.mic.getTracks()) t.stop();
    }
    if (this.ac) {
      try { await this.ac.close(); } catch { /* ignore */ }
    }
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try { this.ws.close(); } catch { /* ignore */ }
    }
    this.capture = null;
    this.source = null;
    this.mic = null;
    this.ac = null;
    this.ws = null;
    this.playbackTime = 0;
    this.setState('idle');
  }

  private async openSocket(): Promise<void> {
    /* Connect to OUR backend voice proxy with the user JWT + mode in the query. */
    /* The backend verifies the JWT, mints the Vertex token, opens the Vertex */
    /* upstream (header server-side) and sends the setup — the browser sends only */
    /* audio + clientContent after this. */
    const session = await requireSession();
    const q = new URLSearchParams({ token: session.access_token, mode: this.mode, ...this.extraQuery });
    if (this.threadId) q.set('threadId', this.threadId);
    const url = `${BACKEND_VOICE_WS}?${q.toString()}`;
    return new Promise((resolve, reject) => {
      const ws = new WebSocket(url);
      ws.binaryType = 'arraybuffer';
      this.ws = ws;

      const onceOpen = () => {
        ws.removeEventListener('open', onceOpen);
        ws.removeEventListener('error', onceErr);
        /* Setup is owned by the backend (it sends Vertex's setup config upstream). */
        resolve();
      };
      const onceErr = (e: Event) => {
        ws.removeEventListener('open', onceOpen);
        ws.removeEventListener('error', onceErr);
        reject(new Error('WebSocket failed: ' + (e as ErrorEvent).message));
      };
      ws.addEventListener('open', onceOpen);
      ws.addEventListener('error', onceErr);
      ws.addEventListener('message', (e) => this.handleServerMessage(e));
      ws.addEventListener('close', (e) => {
        const ce = e as CloseEvent;
        this.lastClose = { code: ce.code, reason: ce.reason || '' };
        console.warn('[voice] ws closed', this.lastClose);
        /* Clean teardown (our stop(), already idle, or a normal 1000 close) is */
        /* not an error — don't scare the user with a red banner. */
        if (this.stopping || this.state === 'idle' || ce.code === 1000) return;
        const why = ce.reason ? `: ${ce.reason}` : '';
        this.cb.onError?.(new Error(`Voice connection closed (code ${ce.code}${why})`));
      });
    });
  }

  private async getMic(): Promise<void> {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error('mediaDevices.getUserMedia unavailable (need HTTPS + a recent browser)');
    }
    if (!window.isSecureContext) {
      throw new Error('Not a secure context (HTTPS required for mic access)');
    }

    let audioInsBefore = -1;
    try {
      const devs = await navigator.mediaDevices.enumerateDevices();
      const audio = devs.filter((d) => d.kind === 'audioinput');
      audioInsBefore = audio.length;
      console.log(
        '[voice] enumerateDevices: audioinput count =',
        audio.length,
        audio.map((d) => ({ id: d.deviceId.slice(0, 12), label: d.label || '(no-label)' })),
      );
    } catch (e) {
      console.warn('[voice] enumerateDevices threw', e);
    }

    const diag = () =>
      ` [ctx=${window.isSecureContext ? 'secure' : 'INSECURE'} ` +
      `audioinputs=${audioInsBefore} ` +
      `ua="${navigator.userAgent.slice(0, 80)}"]`;

    try {
      this.mic = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
        },
      });
      return;
    } catch (e) {
      const name = e instanceof Error ? e.name : '';
      if (name === 'NotAllowedError' || name === 'SecurityError' || name === 'AbortError') {
        throw e;
      }
      console.warn('[voice] strict constraints rejected, retrying with audio:true', e);
    }

    try {
      this.mic = await navigator.mediaDevices.getUserMedia({ audio: true });
      return;
    } catch (e) {
      const name = e instanceof Error ? e.name : 'Error';
      const msg = e instanceof Error ? e.message : String(e);
      /* Wrap with the diagnostic snapshot so the user-facing error tells us */
      /* WHAT the browser sees (device count, secure context, UA). */
      throw new Error(`${name}: ${msg}${diag()}`);
    }
  }

  private async setupAudio(): Promise<void> {
    if (!this.mic) throw new Error('mic not acquired (logic bug — getMic should run first)');

    /* 24 kHz AudioContext matches Gemini's playback rate. Safari historically */
    /* ignored the sampleRate hint; if construction fails outright, fall back */
    /* to the device default and the worklet+playback math adapts. */
    try {
      this.ac = new AudioContext({ sampleRate: 24000 });
    } catch {
      this.ac = new AudioContext();
    }
    if (this.ac.state === 'suspended') await this.ac.resume();

    if (!this.ac.audioWorklet) {
      throw new Error('AudioWorklet unavailable (need Chrome 66+/Firefox 76+/Safari 14.1+)');
    }
    await this.ac.audioWorklet.addModule('/pcm-capture-worklet.js');

    this.source = this.ac.createMediaStreamSource(this.mic);
    this.capture = new AudioWorkletNode(this.ac, 'pcm-capture');
    this.source.connect(this.capture);
    /* Do NOT connect capture to destination — we don't want to monitor our */
    /* own mic locally. Worklet must still process, which it does because it */
    /* returns true from process(); connecting to destination would route */
    /* audio to speakers (echo). */

    this.capture.port.onmessage = (e) => {
      const arr = e.data as ArrayBuffer;
      if (this.micMuted) return; /* drop the chunk before it hits the WS */
      /* HALF-DUPLEX echo guard: don't send mic audio while the assistant is */
      /* speaking. Browser AEC is unreliable (esp. phone speakers), so otherwise */
      /* Naïa's own voice is captured, transcribed as a user turn, and answered — */
      /* an infinite echo loop. We gate on scheduled playback being ahead of the */
      /* clock (assistant talking) plus a short tail so the speaker tail doesn't */
      /* leak back in. */
      if (this.ac && this.playbackTime > this.ac.currentTime + 0.2) return;
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
      const b64 = arrayBufferToBase64(arr);
      this.ws.send(
        JSON.stringify({
          realtimeInput: {
            audio: { data: b64, mimeType: 'audio/pcm;rate=16000' },
          },
        }),
      );
    };
  }

  private handleServerMessage(e: MessageEvent) {
    let msg: unknown;
    if (typeof e.data === 'string') {
      try { msg = JSON.parse(e.data); } catch { return; }
    } else if (e.data instanceof ArrayBuffer) {
      try { msg = JSON.parse(new TextDecoder().decode(e.data)); } catch { return; }
    } else {
      return;
    }

    const m = msg as {
      /* Canal de contrôle du proxy — le fil qu'il a résolu, ou son refus. */
      naia?: { event?: string; threadId?: string; reason?: string };
      setupComplete?: unknown;
      /* Sent shortly before the server ends the session (e.g. duration limit). */
      goAway?: { timeLeft?: string };
      /* Lets us resume the session after a drop (handle stored for future use). */
      sessionResumptionUpdate?: { newHandle?: string; resumable?: boolean };
      serverContent?: {
        modelTurn?: {
          parts?: Array<{
            inlineData?: { data?: string; mimeType?: string };
            text?: string;
          }>;
        };
        turnComplete?: boolean;
        inputTranscription?: { text?: string };
        outputTranscription?: { text?: string };
      };
      /* BidiGenerateContentToolCall — the model is asking us to run a */
      /* function it declared in the token's liveConnectConstraints.tools. */
      toolCall?: {
        functionCalls?: Array<{
          id?: string;
          name?: string;
          args?: Record<string, unknown>;
        }>;
      };
    };

    if (m.naia) {
      /* Les rappels TYPÉS servent aux événements que les deux surfaces
         partagent ; `onServerEvent` porte en plus ceux du budget de parole
         (warn / last_call / stop), qui n'existent que sur le chemin élève. */
      if (m.naia.event === 'ready' && m.naia.threadId) {
        this.cb.onReady?.({ threadId: m.naia.threadId });
      }
      if (m.naia.event === 'refused') {
        this.stopping = true; /* la fermeture qui suit est attendue, pas une panne */
        this.cb.onRefused?.(m.naia.reason ?? 'refused');
      }
      this.cb.onServerEvent?.(m.naia as unknown as Record<string, unknown>);
      return;
    }

    /* Seed prior text turns once the session is ready, before any audio, so */
    /* Naïa keeps the text thread's context (token carries historyConfig). */
    if (m.setupComplete && !this.seeded) {
      this.seeded = true;
      this.seedHistory();
    }

    if (m.sessionResumptionUpdate?.resumable && m.sessionResumptionUpdate.newHandle) {
      this.resumeHandle = m.sessionResumptionUpdate.newHandle;
    }
    if (m.goAway) {
      /* Server is about to end this session (commonly the audio duration cap). */
      /* The close handler fires next; log so the cause is visible. */
      console.warn('[voice] goAway — server ending session', m.goAway);
    }

    if (m.toolCall?.functionCalls?.length) {
      void this.handleToolCall(m.toolCall.functionCalls);
    }

    const sc = m.serverContent;
    if (!sc) return;

    if (sc.inputTranscription?.text) {
      this.pendingUserText += sc.inputTranscription.text;
      this.cb.onTranscript?.(this.pendingUserText, 'user', false);
    }
    if (sc.outputTranscription?.text) {
      this.pendingAssistantText += sc.outputTranscription.text;
      this.cb.onTranscript?.(this.pendingAssistantText, 'assistant', false);
    }

    const parts = sc.modelTurn?.parts ?? [];
    for (const p of parts) {
      if (p.inlineData?.data && (p.inlineData.mimeType ?? '').startsWith('audio/pcm')) {
        this.schedulePlayback(p.inlineData.data);
        this.setState('speaking');
      } else if (p.text) {
        this.pendingAssistantText += p.text;
        this.cb.onTranscript?.(this.pendingAssistantText, 'assistant', false);
      }
    }

    if (sc.turnComplete) {
      if (this.pendingAssistantText) {
        this.cb.onTranscript?.(this.pendingAssistantText, 'assistant', true);
      }
      /* Emit a turn-complete event so the page can persist the pair. */
      /* Guards: only emit if at least one side accumulated text — empty */
      /* turns happen on session start before the user has spoken. */
      if (this.pendingAssistantText || this.pendingUserText) {
        this.cb.onTurnComplete?.({
          user: this.pendingUserText,
          assistant: this.pendingAssistantText,
        });
      }
      this.pendingAssistantText = '';
      this.pendingUserText = '';
      this.setState('listening');
    }
  }

  /* Ship the prior text turns as a single clientContent frame (no turnComplete */
  /* → the model just absorbs them as context and waits for the user's audio). */
  private seedHistory(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN || this.initialHistory.length === 0) return;
    try {
      this.ws.send(
        JSON.stringify({
          clientContent: {
            turns: this.initialHistory.map((t) => ({ role: t.role, parts: [{ text: t.text }] })),
            turnComplete: false,
          },
        }),
      );
    } catch (e) {
      console.warn('[voice] history seed send failed', e);
    }
  }

  private schedulePlayback(b64: string) {
    if (!this.ac) return;
    const pcm = base64ToInt16(b64);
    const float = new Float32Array(pcm.length);
    for (let i = 0; i < pcm.length; i++) {
      const s = pcm[i] ?? 0;
      float[i] = s < 0 ? s / 0x8000 : s / 0x7fff;
    }

    const buf = this.ac.createBuffer(1, float.length, 24000);
    buf.getChannelData(0).set(float);

    const node = this.ac.createBufferSource();
    node.buffer = buf;
    node.connect(this.ac.destination);

    const now = this.ac.currentTime;
    const startAt = Math.max(this.playbackTime, now + 0.02);
    node.start(startAt);
    this.playbackTime = startAt + buf.duration;
  }

  /** Runs the registered tool handler for each functionCall and sends a
   *  BidiGenerateContentToolResponse back to the model. The model pauses
   *  speaking until this lands (gemini-3.1-flash-live-preview is sync-only). */
  private async handleToolCall(
    calls: Array<{ id?: string; name?: string; args?: Record<string, unknown> }>,
  ): Promise<void> {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;

    const responses = await Promise.all(
      calls.map(async (fc) => {
        let response: unknown = { ok: true };
        try {
          if (this.cb.onToolCall && fc.name) {
            const v = await this.cb.onToolCall(fc.name, fc.args ?? {});
            if (v !== undefined) response = v;
          }
        } catch (e) {
          response = { error: e instanceof Error ? e.message : String(e) };
        }
        return { id: fc.id, name: fc.name, response };
      }),
    );

    try {
      this.ws.send(JSON.stringify({ toolResponse: { functionResponses: responses } }));
    } catch (e) {
      console.warn('[voice] toolResponse send failed', e);
    }
  }
}

function arrayBufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    bin += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(bin);
}

function base64ToInt16(b64: string): Int16Array {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  /* Little-endian Int16 view over the same buffer. */
  return new Int16Array(bytes.buffer, bytes.byteOffset, bytes.byteLength / 2);
}
